import React, { useEffect, useState } from 'react';
import api from '../api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Package, FileText, Edit, Download, Eye } from 'lucide-react';

/**
 * Reusable Asset Detail Dialog.
 * Pass `asset` (object) to open; pass null/undefined to close.
 * If `onEdit` is provided, an Edit button is shown that calls onEdit(asset).
 */
const AssetDetailDialog = ({ asset, onClose, onEdit }) => {
  const [bill, setBill] = useState(null);
  const [loadingBill, setLoadingBill] = useState(false);

  useEffect(() => {
    setBill(null);
    if (asset?.has_bill) {
      setLoadingBill(true);
      api.get(`/assets/${asset.id}/bill`)
        .then(r => setBill(r.data))
        .catch(() => {})
        .finally(() => setLoadingBill(false));
    }
  }, [asset]);

  const downloadBill = () => {
    if (!bill?.bill_document) return;
    const a = document.createElement('a');
    a.href = bill.bill_document;
    a.download = bill.bill_filename || 'asset_bill';
    a.click();
  };

  if (!asset) return null;

  return (
    <Dialog open={!!asset} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="asset-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-emerald-700" />
            <span className="font-mono text-blue-700">{asset.asset_code}</span>
            <span className="text-slate-700"> — {asset.asset_name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* KPI summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Kpi label="Type" value={asset.asset_type === 'Other' ? asset.asset_type_other : asset.asset_type} color="purple" />
            <Kpi label="Category" value={asset.category} color="slate" />
            <Kpi label="Quantity" value={asset.quantity ?? 1} color="amber" />
            <Kpi label="Purchase Price" value={`Rs.${(asset.purchase_price || 0).toLocaleString('en-IN')}`} color="blue" />
            <Kpi label="Current Value" value={`Rs.${(asset.current_value || asset.purchase_price || 0).toLocaleString('en-IN')}`} color="emerald" />
          </div>

          {/* Asset Details */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2 text-sm">
            <h3 className="font-semibold text-slate-800 mb-1">Asset Details</h3>
            <Row label="Branch" value={asset.branch_name || '-'} />
            <Row label="Purchase Date" value={asset.purchase_date ? new Date(asset.purchase_date).toLocaleDateString('en-IN') : '-'} />
            <Row label="Life Expectancy" value={`${asset.life_expectancy_years} years`} />
            <Row label="Depreciation Rate" value={asset.depreciation_rate ? `${asset.depreciation_rate}% per year` : 'Not set'} />
            <Row label="Location" value={asset.location || '-'} />
            <Row label="Condition" value={asset.condition} />
            <Row label="Status" value={asset.status || 'Active'} />
            <Row label="Remarks" value={asset.remarks || '-'} />
          </div>

          {/* Bill / GST */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2 text-sm">
            <h3 className="font-semibold text-amber-800 mb-1 flex items-center gap-2">
              <FileText className="w-4 h-4" /> Purchase Bill & GST
            </h3>
            <Row label="Vendor" value={asset.vendor_name || '-'} />
            <Row label="Bill / Invoice No." value={asset.bill_number || '-'} />
            <Row label="Bill Date" value={asset.bill_date ? new Date(asset.bill_date).toLocaleDateString('en-IN') : '-'} />
            <Row label="GST No." value={asset.gst_number ? <span className="font-mono">{asset.gst_number}</span> : '-'} />
            <Row label="GST Amount" value={asset.gst_amount != null ? `Rs.${Number(asset.gst_amount).toLocaleString('en-IN')}` : '-'} />

            <div className="pt-3 border-t border-amber-200">
              {asset.has_bill && bill?.bill_document ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-amber-700">Original Bill:</span>
                    <span className="text-xs text-slate-700">{bill.bill_filename}</span>
                    <Button size="sm" onClick={downloadBill} className="bg-emerald-700 hover:bg-emerald-800 text-white h-7 text-xs" data-testid="download-bill-btn">
                      <Download className="w-3 h-3 mr-1" /> Download
                    </Button>
                    <a href={bill.bill_document} target="_blank" rel="noreferrer" className="text-xs px-2.5 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-1" data-testid="open-bill-btn">
                      <Eye className="w-3 h-3" /> Open in new tab
                    </a>
                  </div>
                  {bill.bill_mime_type?.startsWith('image/') && (
                    <img src={bill.bill_document} alt="Bill" className="max-h-72 rounded border border-amber-300 mt-2" />
                  )}
                  {bill.bill_mime_type === 'application/pdf' && (
                    <iframe src={bill.bill_document} title="Bill PDF" className="w-full h-72 border border-amber-300 rounded mt-2" />
                  )}
                </div>
              ) : asset.has_bill && loadingBill ? (
                <p className="text-xs text-slate-500 italic">Loading bill...</p>
              ) : (
                <p className="text-xs text-slate-500 italic">No purchase bill uploaded for this asset.</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
            {onEdit && (
              <Button onClick={() => onEdit(asset)} className="bg-blue-600 hover:bg-blue-700 text-white">
                <Edit className="w-3.5 h-3.5 mr-1" /> Edit Asset
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Kpi = ({ label, value, color }) => {
  const map = {
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    purple: 'bg-purple-50 border-purple-200 text-purple-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
  };
  return (
    <div className={`rounded-md border ${map[color]} p-2`}>
      <p className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{label}</p>
      <p className="text-sm font-bold truncate">{value || '-'}</p>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-start gap-3 text-xs">
    <span className="text-slate-500 font-medium w-36 flex-shrink-0">{label}:</span>
    <span className="text-slate-900 font-medium">{value}</span>
  </div>
);

export default AssetDetailDialog;
